import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { cn } from '@/lib/cn'

import type { Component, Day, Group } from './api'
import { KIND, longDay, minutesLabel, percent, STATUS } from './meta'
import { Dot, Link, SectionTitle } from './ui'

/** Bars shown on a phone: the last month. */
const PHONE_DAYS = 30

interface Tip {
  day: Day
  box: DOMRect
}

function DayTip({ tip, onEnter, onLeave }: { tip: Tip; onEnter: () => void; onLeave: () => void }) {
  const ref = useRef<HTMLDivElement>(null)
  const [left, setLeft] = useState(0)
  const { day, box } = tip

  useLayoutEffect(() => {
    const width = ref.current?.offsetWidth ?? 260
    const centre = box.left + box.width / 2
    setLeft(Math.min(Math.max(12, centre - width / 2), window.innerWidth - width - 12))
  }, [box])

  const lost = [
    { key: 'major', minutes: day.major },
    { key: 'partial', minutes: day.partial },
    { key: 'degraded', minutes: day.degraded },
    { key: 'maintenance', minutes: day.maintenance },
  ] as const

  return createPortal(
    <div
      ref={ref}
      role="tooltip"
      onPointerEnter={onEnter}
      onPointerLeave={onLeave}
      className="bg-deep/95 fixed z-50 w-[260px] rounded-xl border border-white/10 p-3.5 text-[13px] shadow-2xl shadow-black/50 backdrop-blur-xl"
      style={{ left, top: box.top - 10, transform: 'translateY(-100%)' }}
    >
      <p className="font-semibold text-white">{longDay(day.day)}</p>
      <p className="mt-1.5 flex items-center gap-2">
        <Dot status={day.status} className="size-2" />
        <span className={STATUS[day.status].text}>
          {day.status === 'none' ? 'Nessun dato per questo giorno' : STATUS[day.status].label}
        </span>
        {day.uptime !== null && (
          <span className="text-mist ml-auto tabular-nums">{percent(day.uptime)}</span>
        )}
      </p>
      {lost.some((each) => each.minutes > 0) && (
        <ul className="text-fog mt-2 space-y-0.5 border-t border-white/10 pt-2 text-[12.5px]">
          {lost
            .filter((each) => each.minutes > 0)
            .map((each) => (
              <li key={each.key} className="flex justify-between gap-3">
                <span>{STATUS[each.key].label}</span>
                <span className="tabular-nums">{minutesLabel(each.minutes)}</span>
              </li>
            ))}
        </ul>
      )}
      {day.events.length > 0 && (
        <ul className="mt-2 space-y-1 border-t border-white/10 pt-2">
          {day.events.map((event) => (
            <li key={event.id}>
              <Link
                to={`/eventi/${event.id}`}
                className="text-glow block truncate text-[12.5px] hover:text-white hover:underline"
              >
                {KIND[event.kind]}: {event.title}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>,
    document.body,
  )
}

function Bars({ component }: { component: Component }) {
  const [tip, setTip] = useState<Tip | null>(null)
  const closing = useRef<number | undefined>(undefined)

  const keep = useCallback(() => window.clearTimeout(closing.current), [])
  const close = useCallback(() => {
    window.clearTimeout(closing.current)
    closing.current = window.setTimeout(() => setTip(null), 140)
  }, [])

  useEffect(() => {
    if (!tip) return
    const hide = () => setTip(null)
    window.addEventListener('scroll', hide, { passive: true })
    window.addEventListener('resize', hide)
    return () => {
      window.removeEventListener('scroll', hide)
      window.removeEventListener('resize', hide)
    }
  }, [tip])

  useEffect(() => () => window.clearTimeout(closing.current), [])

  const total = component.days.length
  return (
    <>
      <div className="flex h-9 items-stretch gap-[2px] sm:gap-[3px]" onPointerLeave={close}>
        {component.days.map((day, index) => {
          const look = STATUS[day.status]
          const open = tip?.day.day === day.day
          return (
            <button
              key={day.day}
              type="button"
              aria-label={`${longDay(day.day)}: ${day.status === 'none' ? 'nessun dato' : look.label}`}
              onPointerEnter={(event) => {
                keep()
                setTip({ day, box: event.currentTarget.getBoundingClientRect() })
              }}
              onFocus={(event) => setTip({ day, box: event.currentTarget.getBoundingClientRect() })}
              onBlur={close}
              onClick={(event) => {
                const box = event.currentTarget.getBoundingClientRect()
                setTip((current) => (current?.day.day === day.day ? null : { day, box }))
              }}
              className={cn(
                'min-w-0 flex-1 rounded-[3px] transition-[opacity,transform] duration-150',
                look.bg,
                day.status === 'none' ? 'opacity-100' : 'opacity-85 hover:opacity-100',
                open && 'scale-y-110 opacity-100',
                index < total - PHONE_DAYS && 'max-sm:hidden',
              )}
            />
          )
        })}
      </div>
      {tip && <DayTip tip={tip} onEnter={keep} onLeave={close} />}
    </>
  )
}

function Row({ component, days }: { component: Component; days: number }) {
  const look = STATUS[component.status]
  return (
    <li className="py-5 first:pt-0 last:pb-0">
      <div className="mb-3 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h3 className="text-[15px] font-semibold text-white">{component.name}</h3>
          {component.description && (
            <p className="text-mist mt-0.5 text-[12.5px] leading-snug">{component.description}</p>
          )}
        </div>
        <span className={cn('flex shrink-0 items-center gap-2 text-[13px] font-medium', look.text)}>
          <Dot status={component.status} pulse={component.status !== 'operational'} />
          {look.label}
        </span>
      </div>
      <Bars component={component} />
      <div className="text-mist/80 mt-2 flex items-center gap-3 text-[11.5px]">
        <span className="max-sm:hidden">{days} giorni fa</span>
        <span className="sm:hidden">{PHONE_DAYS} giorni fa</span>
        <span className="h-px flex-1 bg-white/10" aria-hidden />
        <span className="text-fog tabular-nums">
          {component.uptime === null ? 'uptime in calcolo' : `${percent(component.uptime)} uptime`}
        </span>
        <span className="h-px flex-1 bg-white/10" aria-hidden />
        <span>Oggi</span>
      </div>
    </li>
  )
}

function Legend() {
  const shown = ['operational', 'degraded', 'partial', 'major', 'maintenance', 'none'] as const
  return (
    <ul className="text-mist flex flex-wrap gap-x-4 gap-y-1.5 text-[12px]">
      {shown.map((status) => (
        <li key={status} className="inline-flex items-center gap-1.5">
          <span className={cn('h-3 w-1.5 rounded-[2px]', STATUS[status].bg)} aria-hidden />
          {status === 'none' ? 'Nessun dato' : STATUS[status].label}
        </li>
      ))}
    </ul>
  )
}

/** Every component, by group, with its days. */
export function Uptime({ groups, days }: { groups: Group[]; days: number }) {
  if (!groups.length) return null
  return (
    <section aria-labelledby="servizi">
      <SectionTitle aside={<span className="text-mist text-[12px]">Ultimi {days} giorni</span>}>
        <span id="servizi">Servizi</span>
      </SectionTitle>
      <div className="space-y-4">
        {groups.map((group) => (
          <div key={group.name || '—'} className="card p-5 sm:p-6">
            {group.name && (
              <div className="mb-5 flex items-center justify-between gap-3 border-b border-white/[0.06] pb-4">
                <p className="text-fog text-[11px] font-semibold tracking-[0.24em] uppercase">
                  {group.name}
                </p>
                <span
                  className={cn('flex items-center gap-2 text-[12px]', STATUS[group.status].text)}
                >
                  <Dot status={group.status} className="size-2" />
                  {group.status === 'operational' ? 'Tutto operativo' : STATUS[group.status].label}
                </span>
              </div>
            )}
            <ul className="divide-y divide-white/[0.06]">
              {group.components.map((component) => (
                <Row key={component.id} component={component} days={days} />
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="mt-4">
        <Legend />
      </div>
    </section>
  )
}
